import { AssistantThread } from 'types/AssistantTypes';
import RestService from './RestService';
import { ApiRoutes } from './apiRoutes';

export const AssistantService = {
  newThread: async (): Promise<AssistantThread> => {
    return RestService.post(ApiRoutes.THREADS);
  },

  fetchReportData: async (portfolioId: string, queryParams?: { backtest_start?: string; backtest_end?: string }): Promise<any> => {
    return RestService.get(ApiRoutes.FINECO_PORTFOLIO_REPORT_DATA(portfolioId), queryParams);
  },

  createProductCardPdf: async (isin: string): Promise<any> => {
    // The product-card buttons show their own error toast.
    return RestService.download(ApiRoutes.CREATE_PRODUCT_CARD_PDF(isin), {
      suppressGlobalErrorToast: true,
    });
  },

  createProductCardComparePdf: async (isins: string[]): Promise<any> => {
    return RestService.post(
      ApiRoutes.CREATE_PRODUCT_CARD_COMPARE_PDF,
      {
        isins: isins,
      },
      {
        responseType: 'blob',
        suppressGlobalErrorToast: true,
      },
    );
  },

  fetchReportParameters: async (portfolioId: string): Promise<any> => {
    return RestService.get(ApiRoutes.FINECO_PORTFOLIO_REPORT_PARAMETERS(portfolioId));
  },

  createPdf: async (portfolioId: string, reportParameters: any, queryParams?: { backtest_start?: string; backtest_end?: string }): Promise<any> => {
    const params: any = { responseType: "blob" };

    if (queryParams) {
      params.params = queryParams;
    }

    return RestService.post(
      ApiRoutes.FINECO_PORTFOLIO_REPORT_PDF(portfolioId),
      {
        report_parameters: reportParameters,
      },
      params,
    );
  },

  createExcel: async (portfolioId: string): Promise<any> => {
    const response = await RestService.download(ApiRoutes.FINECO_PORTFOLIO_REPORT_EXCEL(portfolioId));
    return response.data;
  },

  saveInMyPortfolio: async (portfolioId: string, threadId?: string): Promise<any> => {
    // FinecoPortfolio shows its own error toast with the backend message.
    const config = { ...(threadId && { params: { thread_id: threadId } }), suppressGlobalErrorToast: true };
    return RestService.post(ApiRoutes.FINECO_PORTFOLIO_SAVE(portfolioId), undefined, config);
  },

  importToXnet: async (portfolioId: string, threadId?: string): Promise<any> => {
    const config = threadId ? { params: { thread_id: threadId } } : undefined;
    return RestService.post(ApiRoutes.FINECO_PORTFOLIO_IMPORT_XNET(portfolioId), undefined, config);
  },

  getPortfolioActions: async (threadId: string): Promise<any> => {
    return RestService.get(ApiRoutes.FINECO_PORTFOLIO_ACTIONS, { thread_id: threadId });
  },

  createComparisonPdf: async (
    portfolioId1: string,
    portfolioId2: string,
    reportParameters: any,
    queryParams?: { backtest_start?: string; backtest_end?: string },
    portfolioLabels?: string[],
    portfolioShortLabels?: string[],
  ): Promise<any> => {
    const params: any = { responseType: 'blob' };

    if (queryParams) {
      params.params = queryParams;
    }

    return RestService.post(
      ApiRoutes.FINECO_PORTFOLIOS_COMPARE,
      {
        portfolio_id_1: portfolioId1,
        portfolio_id_2: portfolioId2,
        report_parameters: reportParameters,
        ...(portfolioLabels && { portfolio_labels: portfolioLabels }),
        ...(portfolioShortLabels && { portfolio_short_labels: portfolioShortLabels }),
      },
      params,
    );
  },

  fetchPortfolioStory: async (portfolioId: string): Promise<any> => {
    return RestService.get(ApiRoutes.FINECO_PORTFOLIO_STORY(portfolioId));
  },

  fetchProductsCostData: async (isins: string[]): Promise<any> => {
    return RestService.post(ApiRoutes.COSTS_PRODUCTS_DISPLAY, { isins });
  },
};
